import React, { useState, useEffect, useCallback } from 'react';
import { Save, Trash2, Eye, Link as LinkIcon, ExternalLink, Upload, X, Download, Copy } from 'lucide-react';
import PopupComponent from '../../../../components/PopupComponent';
import Inputs from '../../../../components/inputs';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import GenericConfirmationModal from '../../../../components/GenericConfirmationModal';
import { Logger } from '../../../../utils/logger';
import { DigitalCardData } from '../../types';
import {
  createDigitalCard,
  updateDigitalCard,
  deleteDigitalCard,
  getDigitalCard
} from '../../../../services/digitalCardService';
import { getSignedUrl, uploadToSignedUrl } from '../../../../services/utilityService';
import { BUCKET_URL, WEB_APP_URL } from '../../../../config/config';

interface DigitalCardContentProps {
  openDigitalCardBox: boolean;
  setOpenDigitalCardBox: (value: boolean) => void;
  activeId: string;
  digitalCardData: DigitalCardData;
  handleDigitalCardForm: (key: string, value: string) => void;
  clearData: () => void;
  onSuccessfulSubmit?: () => Promise<void>;
}

const DigitalCardContent: React.FC<DigitalCardContentProps> = ({
  openDigitalCardBox,
  setOpenDigitalCardBox,
  activeId,
  digitalCardData,
  handleDigitalCardForm,
  clearData,
  onSuccessfulSubmit
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string>('');
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [pictureFile, setPictureFile] = useState<File | null>(null);

  useEffect(() => {
    if (openDigitalCardBox) {
      setHasUnsavedChanges(false);
    }
  }, [openDigitalCardBox]);

  const validateForm = useCallback(() => {
    const errors: string[] = [];
    if (!digitalCardData.name?.trim()) errors.push("Name is required");
    if (!digitalCardData.email?.trim()) errors.push("Email is required");
    if (!digitalCardData.phone?.trim()) errors.push("Phone is required");

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (digitalCardData.email && !emailRegex.test(digitalCardData.email)) {
      errors.push("Please enter a valid email address");
    }

    // Removed URL validations - handled on PDF generation side

    return errors;
  }, [digitalCardData]);

  const handleInputChange = (key: string, value: string) => {
    handleDigitalCardForm(key, value);
    setHasUnsavedChanges(true);
  };

  const handleProfilePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setPictureFile(file);

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicture(reader.result as string);
    };
    reader.readAsDataURL(file);
    setHasUnsavedChanges(true);
  };

  const uploadProfilePicture = async (): Promise<string> => {
    if (!pictureFile) return '';

    setUploadingPicture(true);
    try {
      // Get signed URL for digital_card type
      const signedUrlResponse = await getSignedUrl({
        recordId: activeId || 'temp',
        fileName: pictureFile.name,
        type: 'digital_card',
        mimeType: pictureFile.type
      });

      Logger.log('[uploadProfilePicture] Signed URL response:', signedUrlResponse);

      // Upload to S3
      await uploadToSignedUrl(pictureFile, signedUrlResponse.data.signedUrl);

      Logger.log('[uploadProfilePicture] Upload successful, filePath:', signedUrlResponse.data.filePath);

      return signedUrlResponse.data.filePath;
    } catch (error) {
      Logger.error('[uploadProfilePicture] Error:', error);
      toast.error('Failed to upload profile picture');
      throw error;
    } finally {
      setUploadingPicture(false);
    }
  };

  const removeProfilePicture = () => {
    setProfilePicture('');
    setPictureFile(null);
    setHasUnsavedChanges(true);
  };

  const handleSave = useCallback(async () => {
    Logger.log('[handleSave] Called with data:', digitalCardData);

    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload profile picture if new one is selected
      let profilePicturePath = '';
      if (pictureFile) {
        Logger.log('[handleSave] Uploading profile picture...');
        profilePicturePath = await uploadProfilePicture();
        Logger.log('[handleSave] Profile picture uploaded:', profilePicturePath);
      }

      // Prepare payload matching the mobile app API structure
      const payload: any = {
        name: digitalCardData.name,
        email: digitalCardData.email,
        phone: digitalCardData.phone,
        whatsapp: digitalCardData.whatsapp || '',
        designation: digitalCardData.designation || '',
        companyName: digitalCardData.companyName || '',
        companyAddress: digitalCardData.companyAddress || '',
        briefDescription: digitalCardData.briefDescription || '',
        website: digitalCardData.website || '',
        facebook: digitalCardData.facebook || '',
        instagram: digitalCardData.instagram || '',
        linkedIn: digitalCardData.linkedIn || '',
        googleCalendar: digitalCardData.googleCalendar || '',
      };

      // Add profile picture if uploaded
      if (profilePicturePath) {
        payload.profilePicture = profilePicturePath;
      }

      Logger.log('[handleSave] Prepared API payload:', payload);

      let response;

      // Check if we're updating (activeId exists and has a digital card already)
      if (activeId && activeId.length > 1) {
        Logger.log(`[handleSave] UPDATING DIGITAL CARD - ID: ${activeId}`);
        response = await updateDigitalCard(payload);
      } else {
        Logger.log('[handleSave] CREATING NEW DIGITAL CARD');
        response = await createDigitalCard(payload);
      }

      Logger.log(`[handleSave] API response:`, response);

      if (response?.status === 200 || response?.data?.status) {
        toast.success('Digital card saved successfully!');
        setHasUnsavedChanges(false);
        setPictureFile(null); // Clear picture file after save
        setOpenDigitalCardBox(false);

        if (onSuccessfulSubmit) {
          await onSuccessfulSubmit();
        }
      } else {
        console.error('[handleSave] API returned error:', response);
        toast.error('Error saving digital card');
      }
    } catch (error: any) {
      console.error('[handleSave] Error saving digital card:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save digital card';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [digitalCardData, validateForm, activeId, setOpenDigitalCardBox, onSuccessfulSubmit, pictureFile, uploadProfilePicture]);

  const handleDelete = useCallback(async () => {
    if (!activeId) return;

    try {
      const response = await deleteDigitalCard();

      if (response.status === 200 || response.data?.status) {
        toast.success('Digital card deleted successfully');
        setOpenDigitalCardBox(false);

        if (onSuccessfulSubmit) {
          await onSuccessfulSubmit();
        }
      } else {
        toast.error('Failed to delete digital card');
      }
    } catch (error: any) {
      console.error('Error deleting digital card:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error deleting digital card';
      toast.error(errorMessage);
    }
  }, [activeId, setOpenDigitalCardBox, onSuccessfulSubmit]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      setOpenDigitalCardBox(false);
      clearData();
    }
  }, [hasUnsavedChanges, setOpenDigitalCardBox, clearData]);

  const handleConfirmClose = useCallback(() => {
    setOpenDigitalCardBox(false);
    clearData();
  }, [setOpenDigitalCardBox, clearData]);

  const handlePreview = useCallback(() => {
    if (digitalCardData.uniqueLinkId) {
      setShowPreviewModal(true);
    } else {
      toast.error('Please save the digital card first to generate a preview link');
    }
  }, [digitalCardData.uniqueLinkId]);

  const copyShareLink = useCallback(() => {
    if (digitalCardData.uniqueLinkId) {
      const shareLink = `${WEB_APP_URL}/share/Card/${digitalCardData.uniqueLinkId}`;
      navigator.clipboard.writeText(shareLink);
      toast.success('Link copied to clipboard!');
    }
  }, [digitalCardData.uniqueLinkId]);

  const handleDownloadPDF = useCallback(async () => {
    if (!digitalCardData.cardUrl) {
      toast.error('PDF not available. Please save the card first.');
      return;
    }

    try {
      const pdfUrl = `${BUCKET_URL}${digitalCardData.cardUrl}`;
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${digitalCardData.name || 'BusinessCard'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      Logger.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  }, [digitalCardData.cardUrl, digitalCardData.name]);

  const headerActions = (
    <ButtonGroup>
      {digitalCardData.uniqueLinkId && (
        <Button onClick={handlePreview}>
          <Eye size={16} /> Preview
        </Button>
      )}

      {activeId && (
        <Button
          onClick={() => setShowDeleteModal(true)}
          style={{
            backgroundColor: '#fee2e2',
            color: '#dc2626'
          }}
        >
          <Trash2 size={16} /> Delete
        </Button>
      )}

      <Button primary onClick={handleSave} disabled={isSubmitting}>
        {isSubmitting ? (
          "Saving..."
        ) : (
          <>
            <Save size={16} /> Save Card
          </>
        )}
      </Button>
    </ButtonGroup>
  );

  return (
    <>
      <PopupComponent
        isOpen={openDigitalCardBox}
        onClose={handleClose}
        title={activeId ? "Edit Digital Card" : "Create Digital Card"}
        size="lg"
        loading={isSubmitting}
        headerActions={headerActions}
      >
        <StyledForm>
          <FormSection>
            <SectionTitle>Profile Picture</SectionTitle>
            <ProfilePictureContainer>
              <ProfilePicturePreview>
                {profilePicture || digitalCardData.cardUrl ? (
                  <ProfileImage
                    src={profilePicture || `${BUCKET_URL}${digitalCardData.cardUrl}`}
                    alt="Profile"
                  />
                ) : (
                  <ProfilePlaceholder>
                    <Upload size={40} />
                    <span>No Image</span>
                  </ProfilePlaceholder>
                )}
              </ProfilePicturePreview>
              <ProfilePictureActions>
                <input
                  type="file"
                  id="profile-picture-input"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleProfilePictureChange}
                />
                <UploadButton
                  onClick={() => document.getElementById('profile-picture-input')?.click()}
                  disabled={uploadingPicture}
                >
                  <Upload size={16} />
                  {uploadingPicture ? 'Uploading...' : 'Upload Picture'}
                </UploadButton>
                {(profilePicture || pictureFile) && (
                  <RemoveButton onClick={removeProfilePicture}>
                    <X size={16} />
                    Remove
                  </RemoveButton>
                )}
                <ProfilePictureHint>
                  Max size: 5MB. Supported: JPG, PNG, GIF
                </ProfilePictureHint>
              </ProfilePictureActions>
            </ProfilePictureContainer>
          </FormSection>

          <FormSection>
            <SectionTitle>Personal Details</SectionTitle>

            <Inputs
              label="Name"
              name="name"
              type="name"
              value={digitalCardData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter full name"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="name"
            />

            <Inputs
              label="Email"
              name="email"
              type="email"
              value={digitalCardData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter email address"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="email"
            />

            <Inputs
              label="Phone Number"
              name="phone"
              type="phone"
              value={digitalCardData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="Enter phone number"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="phone"
            />

            <Inputs
              label="WhatsApp Number"
              name="whatsapp"
              type="phone"
              value={digitalCardData.whatsapp || ''}
              onChange={(e) => handleInputChange('whatsapp', e.target.value)}
              placeholder="Enter WhatsApp number"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="whatsapp"
            />
          </FormSection>

          <FormSection>
            <SectionTitle>Professional Details</SectionTitle>

            <Inputs
              label="Designation"
              name="designation"
              type="text"
              value={digitalCardData.designation || ''}
              onChange={(e) => handleInputChange('designation', e.target.value)}
              placeholder="Enter designation"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="designation"
            />

            <Inputs
              label="Company Name"
              name="companyName"
              type="text"
              value={digitalCardData.companyName || ''}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              placeholder="Enter company name"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="companyName"
            />

            <Inputs
              label="Company Address"
              name="companyAddress"
              type="text"
              value={digitalCardData.companyAddress || ''}
              onChange={(e) => handleInputChange('companyAddress', e.target.value)}
              placeholder="Enter company address"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="companyAddress"
            />

            <Inputs
              label="Brief Description"
              name="briefDescription"
              type="text"
              value={digitalCardData.briefDescription || ''}
              onChange={(e) => handleInputChange('briefDescription', e.target.value)}
              placeholder="Enter a brief introduction"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="briefDescription"
            />
          </FormSection>

          <FormSection>
            <SectionTitle>Social Links</SectionTitle>

            <Inputs
              label="Website"
              name="website"
              type="url"
              value={digitalCardData.website || ''}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder="https://example.com"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="website"
            />

            <Inputs
              label="Facebook"
              name="facebook"
              type="url"
              value={digitalCardData.facebook || ''}
              onChange={(e) => handleInputChange('facebook', e.target.value)}
              placeholder="https://facebook.com/yourprofile"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="facebook"
            />

            <Inputs
              label="Instagram"
              name="instagram"
              type="url"
              value={digitalCardData.instagram || ''}
              onChange={(e) => handleInputChange('instagram', e.target.value)}
              placeholder="https://instagram.com/yourprofile"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="instagram"
            />

            <Inputs
              label="LinkedIn"
              name="linkedIn"
              type="url"
              value={digitalCardData.linkedIn || ''}
              onChange={(e) => handleInputChange('linkedIn', e.target.value)}
              placeholder="https://linkedin.com/in/yourprofile"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="linkedIn"
            />

            <Inputs
              label="Google Calendar"
              name="googleCalendar"
              type="url"
              value={digitalCardData.googleCalendar || ''}
              onChange={(e) => handleInputChange('googleCalendar', e.target.value)}
              placeholder="Google Calendar booking link"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="googleCalendar"
            />
          </FormSection>

          {digitalCardData.uniqueLinkId && (
            <ShareSection>
              <SectionTitle>Share Your Card</SectionTitle>
              <ShareLinkContainer>
                <ShareLink>
                  {`${WEB_APP_URL}/share/Card/${digitalCardData.uniqueLinkId}`}
                </ShareLink>
                <CopyButton onClick={copyShareLink}>
                  <LinkIcon size={16} /> Copy Link
                </CopyButton>
              </ShareLinkContainer>
            </ShareSection>
          )}
        </StyledForm>
      </PopupComponent>

      <GenericConfirmationModal
        showModal={showConfirmModal}
        toggleModal={() => setShowConfirmModal(false)}
        onConfirmation={handleConfirmClose}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmText="Yes, Close"
        cancelText="Cancel"
        isWarning={true}
      />

      <GenericConfirmationModal
        showModal={showDeleteModal}
        toggleModal={() => setShowDeleteModal(false)}
        onConfirmation={handleDelete}
        title="Delete Digital Card"
        message="Are you sure you want to delete this digital card? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isWarning={true}
      />

      {showPreviewModal && digitalCardData.uniqueLinkId && (
        <PopupComponent
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          title="Digital Card Preview"
          size="lg"
        >
          <PreviewContainer>
            <PreviewActions>
              <Button
                onClick={() => window.open(`/share/Card/${digitalCardData.uniqueLinkId}`, '_blank')}
              >
                <ExternalLink size={16} /> Open in New Tab
              </Button>

              <Button onClick={copyShareLink} primary>
                <Copy size={16} /> Copy Link
              </Button>

              {digitalCardData.cardUrl && (
                <Button onClick={handleDownloadPDF}>
                  <Download size={16} /> Download PDF
                </Button>
              )}
            </PreviewActions>
            <PreviewFrame
              src={`/share/Card/${digitalCardData.uniqueLinkId}`}
              title="Digital Card Preview"
            />
          </PreviewContainer>
        </PopupComponent>
      )}
    </>
  );
};

export default DigitalCardContent;

// Styled Components
const StyledForm = styled.div`
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
`;

const FormSection = styled.div`
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-right: 48px;
`;

const Button = styled.button<{ primary?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 8px;

  ${(props) =>
    props.primary
      ? `
    background-color: #3498db;
    color: white;
    &:hover {
      background-color: #2980b9;
    }
    svg {
      color: white;
    }
  `
      : `
    background-color: #e9ecef;
    color: #495057;
    &:hover {
      background-color: #dee2e6;
    }
  `}

  svg {
    margin-right: 6px;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ShareSection = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-top: 20px;
`;

const ShareLinkContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ShareLink = styled.div`
  flex: 1;
  padding: 12px;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #2980b9;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 70vh;
`;

const PreviewActions = styled.div`
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const PreviewFrame = styled.iframe`
  flex: 1;
  border: none;
  width: 100%;
`;

const ProfilePictureContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProfilePicturePreview = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProfilePlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  gap: 8px;

  span {
    font-size: 12px;
    font-weight: 500;
  }
`;

const ProfilePictureActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Gilroy', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: fit-content;

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 8px;
  font-family: 'Gilroy', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: fit-content;

  &:hover {
    background: #fecaca;
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProfilePictureHint = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  font-family: 'Gilroy', sans-serif;
`;
